import { createRouter, createWebHistory } from 'vue-router'
import CloudEntryPage from '@/views/CloudEntryPage.vue'
import ProjectWorkspaceLayout from '@/views/ProjectWorkspaceLayout.vue'
import ProjectBrainPage from '@/views/ProjectBrainPage.vue'
import PersonaMatrixPage from '@/views/PersonaMatrixPage.vue'
import DailyTasksPage from '@/views/DailyTasksPage.vue'
import ProjectUserAppsPage from '@/views/ProjectUserAppsPage.vue'
import TenantSelectPage from '@/views/TenantSelectPage.vue'
import WaitingForProjectPage from '@/views/WaitingForProjectPage.vue'
import AssignmentReviewPage from '@/views/AssignmentReviewPage.vue'
import EnterpriseHomePage from '@/views/EnterpriseHomePage.vue'
import SuperadminHomePage from '@/views/SuperadminHomePage.vue'
import EnterpriseProfilePage from '@/views/EnterpriseProfilePage.vue'
import MarketAnalysisPage from '@/views/MarketAnalysisPage.vue'
import MaterialLibraryPage from '@/views/MaterialLibraryPage.vue'
import NotFoundPage from '@/views/NotFoundPage.vue'
import MemberInvitationPage from '@/views/MemberInvitationPage.vue'
import ForcedPasswordChangePage from '@/views/ForcedPasswordChangePage.vue'
import { authStore } from '@/stores/auth.js'
import { legacyProjectRoute } from './projectRoutes.js'

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: '/',
      redirect: '/login',
    },
    {
      path: '/login',
      name: 'login',
      component: CloudEntryPage,
      meta: { title: 'AI 营销项目经理登录', loginWorkspace: 'project_manager' },
    },
    {
      path: '/login/superadmin',
      name: 'login-superadmin',
      component: CloudEntryPage,
      meta: { title: '第一级 · 平台超级管理员登录', loginWorkspace: 'superadmin' },
    },
    {
      path: '/login/enterprise-admin',
      name: 'login-enterprise-admin',
      component: CloudEntryPage,
      meta: { title: 'AI 企业总部登录', loginWorkspace: 'enterprise_admin' },
    },
    {
      path: '/invite/:token',
      name: 'member-invitation',
      component: MemberInvitationPage,
      meta: { title: '加入企业空间' },
    },
    {
      path: '/select-enterprise',
      name: 'select-enterprise',
      component: TenantSelectPage,
      meta: { title: '选择企业', requiresAuth: true },
    },
    {
      path: '/project-manager/projects/:projectId',
      name: 'project-manager-workspace',
      component: ProjectWorkspaceLayout,
      redirect: (to) => `/project-manager/projects/${to.params.projectId}/dashboard`,
      meta: { title: '工作台', requiresAuth: true, requiresAssignedProject: true, workspaceType: 'project_manager', workspaceChrome: true },
      children: [
        { path: 'dashboard', name: 'project-manager-dashboard', component: ProjectBrainPage, meta: { title: '项目大脑' } },
        { path: 'company/basic', name: 'project-manager-company-basic', component: EnterpriseProfilePage, meta: { title: '企业基本信息' } },
        { path: 'company/market', name: 'project-manager-company-market', component: MarketAnalysisPage, meta: { title: '企业市场分析' } },
        { path: 'materials', name: 'project-manager-materials', component: MaterialLibraryPage, meta: { title: '企业真实素材库' } },
        { path: 'personas', name: 'project-manager-personas', component: PersonaMatrixPage, meta: { title: '素人20人设矩阵' } },
        { path: 'user-apps', name: 'project-manager-user-apps', component: ProjectUserAppsPage, meta: { title: '我的用户 App' } },
        { path: 'daily-tasks', name: 'project-manager-daily-tasks', component: DailyTasksPage, meta: { title: '每日任务中心' } },
      ],
    },
    {
      path: '/workspace/:pathMatch(.*)*',
      name: 'legacy-workspace',
      component: ProjectBrainPage,
      meta: { title: '工作台', requiresAuth: true, legacyWorkspace: true },
    },
    {
      path: '/enterprise-profile',
      redirect: '/workspace/company/basic',
    },
    {
      path: '/market-analysis',
      redirect: '/workspace/company/market',
    },
    {
      path: '/materials',
      redirect: '/workspace/materials',
    },
    {
      path: '/waiting-for-project',
      name: 'waiting-for-project',
      component: WaitingForProjectPage,
      meta: { title: '等待项目分配', requiresAuth: true },
    },
    {
      path: '/assignment-review',
      name: 'assignment-review',
      component: AssignmentReviewPage,
      meta: { title: '项目归属待确认', requiresAuth: true },
    },
    {
      path: '/superadmin/dashboard',
      name: 'superadmin-dashboard',
      component: SuperadminHomePage,
      meta: { title: '第一级 · 平台总览', requiresAuth: true, workspaceType: 'superadmin', superadminSection: 'overview' },
    },
    { path: '/superadmin/change-password', name: 'superadmin-change-password', component: ForcedPasswordChangePage, meta: { title: '修改临时密码', requiresAuth: true, workspaceType: 'superadmin', passwordChangeRoute: true } },
    {
      path: '/superadmin/enterprises/:tenantId/:tab(overview|managers|apps|accounts|audit)',
      name: 'superadmin-enterprise-detail',
      component: SuperadminHomePage,
      meta: { title: '第一级 · 企业详情', requiresAuth: true, workspaceType: 'superadmin', superadminSection: 'enterprises' },
    },
    {
      path: '/superadmin/:section(enterprises|operations|ai-api|audit)',
      name: 'superadmin-section',
      component: SuperadminHomePage,
      meta: { title: '第一级 · 平台总管理后台', requiresAuth: true, workspaceType: 'superadmin' },
    },
    { path: '/superadmin/tasks', redirect: { path: '/superadmin/operations', query: { view: 'tasks' } } },
    { path: '/superadmin/content', redirect: { path: '/superadmin/operations', query: { view: 'content' } } },
    { path: '/superadmin/usage', redirect: { path: '/superadmin/operations', query: { view: 'usage' } } },
    { path: '/superadmin/managers', redirect: { path: '/superadmin/enterprises', query: { tab: 'managers' } } },
    { path: '/superadmin/apps', redirect: { path: '/superadmin/enterprises', query: { tab: 'apps' } } },
    { path: '/superadmin/accounts', redirect: { path: '/superadmin/audit', query: { view: 'accounts' } } },
    { path: '/superadmin/alerts', redirect: { path: '/superadmin/operations', query: { view: 'alerts' } } },
    { path: '/superadmin/system', redirect: '/superadmin/ai-api' },
    {
      path: '/enterprise-admin/dashboard',
      name: 'enterprise-admin-dashboard',
      component: EnterpriseHomePage,
      meta: { title: 'AI 企业总部管理后台', requiresAuth: true, workspaceType: 'enterprise_admin' },
    },
    {
      path: '/enterprise-admin/:section(team|execution|finance|governance)',
      name: 'enterprise-admin-section',
      component: EnterpriseHomePage,
      meta: { title: 'AI 企业总部管理后台', requiresAuth: true, workspaceType: 'enterprise_admin' },
    },
    { path: '/enterprise-admin/managers', redirect: { path: '/enterprise-admin/team', query: { view: 'managers' } } },
    { path: '/enterprise-admin/projects', redirect: { path: '/enterprise-admin/team', query: { view: 'projects' } } },
    { path: '/enterprise-admin/accounts', redirect: { path: '/enterprise-admin/team', query: { view: 'accounts' } } },
    { path: '/enterprise-admin/apps', redirect: { path: '/enterprise-admin/execution', query: { view: 'apps' } } },
    { path: '/enterprise-admin/operations', redirect: { path: '/enterprise-admin/execution', query: { view: 'tasks' } } },
    { path: '/enterprise-admin/publication', redirect: { path: '/enterprise-admin/execution', query: { view: 'publication' } } },
    { path: '/enterprise-admin/usage', redirect: { path: '/enterprise-admin/finance', query: { view: 'costs' } } },
    { path: '/enterprise-admin/alerts', redirect: { path: '/enterprise-admin/finance', query: { view: 'alerts' } } },
    { path: '/enterprise-admin/settings', redirect: { path: '/enterprise-admin/governance', query: { view: 'settings' } } },
    { path: '/enterprise-admin/audit', redirect: { path: '/enterprise-admin/governance', query: { view: 'audit' } } },
    { path: '/enterprise-admin/change-password', name: 'enterprise-admin-change-password', component: ForcedPasswordChangePage, meta: { title: '修改临时密码', requiresAuth: true, workspaceType: 'enterprise_admin', passwordChangeRoute: true } },
    { path: '/project-manager/change-password', name: 'project-manager-change-password', component: ForcedPasswordChangePage, meta: { title: '修改临时密码', requiresAuth: true, workspaceType: 'project_manager', passwordChangeRoute: true } },
    {
      path: '/enterprise',
      name: 'legacy-enterprise-home',
      component: EnterpriseHomePage,
      meta: { title: '企业管理', requiresAuth: true, legacyWorkspace: true },
    },
    {
      path: '/:pathMatch(.*)*',
      name: 'not-found',
      component: NotFoundPage,
      meta: { title: '页面不存在' },
    },
  ],
  scrollBehavior: () => ({ top: 0 }),
})

function loginRouteForWorkspace(workspaceType) {
  if (workspaceType === 'superadmin') return 'login-superadmin'
  if (workspaceType === 'enterprise_admin') return 'login-enterprise-admin'
  return 'login'
}

router.beforeEach(async (to) => {
  if (!to.meta.requiresAuth) {
    return true
  }
  if (!await authStore.ensureSession()) {
    return { name: loginRouteForWorkspace(to.meta.workspaceType), query: { next: to.fullPath } }
  }
  if (authStore.state.user?.must_change_password && !to.meta.passwordChangeRoute) {
    if (to.meta.workspaceType === 'superadmin') return { name: 'superadmin-change-password' }
    if (to.meta.workspaceType === 'enterprise_admin') return { name: 'enterprise-admin-change-password' }
    return { name: 'project-manager-change-password' }
  }
  if (to.meta.passwordChangeRoute) return true
  if (!authStore.state.selectedTenant && !authStore.state.isSystemAdmin) {
    const target = await authStore.prepareDefaultTenant()
    const assignedWorkspaceTarget = (to.meta.requiresAssignedProject || to.meta.legacyWorkspace)
      && authStore.state.assignment?.state === 'assigned'
    if (target !== to.path && !assignedWorkspaceTarget) return target
  }
  const authoritativeRoute = authStore.routeForCurrentState()
  const assignedProjectRoute = to.meta.requiresAssignedProject
    && authStore.state.assignment?.state === 'assigned'
    && String(to.params.projectId) === String(authStore.state.assignment?.project?.id)
  const allowedWorkspace = (to.meta.workspaceType === 'superadmin' && authStore.state.isSystemAdmin)
    || (to.meta.workspaceType === 'enterprise_admin' && ['owner', 'admin'].includes(authStore.state.selectedTenant?.role_code))
    || assignedProjectRoute
  if (to.meta.legacyWorkspace) return legacyProjectRoute(to, authStore.state.assignment, authoritativeRoute)
  if (to.path !== authoritativeRoute && to.name !== 'select-enterprise' && !allowedWorkspace) {
    return authoritativeRoute
  }
  return true
})

router.afterEach((to, from, failure) => {
  if (failure) return
  if (String(to.name || '').startsWith('login')) return
  document.title = to.meta.title ? `${to.meta.title} · AI Marketing SaaS` : 'AI Marketing SaaS'
})

export default router
